import { LinkEventMetadata } from 'react-native-plaid-link-sdk';
import { PlaidLinkOnEventMetadata } from 'react-plaid-link';
import {
  PLAID_LINK_OPENED,
  PLAID_LINK_STEP_COMPLETED,
} from 'shared/analytics/events/externalAccount';
import { VaroEvent } from 'shared/analytics/types/events';
import { PlaidIntentTypes } from 'shared/hooks/usePlaidV2/types';

const isWebEventMetadata = (
  metadata: LinkEventMetadata | PlaidLinkOnEventMetadata,
): metadata is PlaidLinkOnEventMetadata => 'link_session_id' in metadata;

const firedEvents = new Set<string>();
let currentSessionId: string | undefined;

export const resetPlaidEventDedup = () => {
  firedEvents.clear();
  currentSessionId = undefined;
};

const versionUnknown = 'unknown';

interface LogOnEventAnalyticsParams {
  logEvent: (
    event: VaroEvent,
    customProperties?: Record<string, unknown>,
  ) => Promise<void>;
  eventName: string;
  metadata: LinkEventMetadata | PlaidLinkOnEventMetadata;
  intent: PlaidIntentTypes | undefined;
  sourceName: string | undefined;
  plaidIntegrationVersion?: string;
}

export const logOnEventAnalytics = (params: LogOnEventAnalyticsParams) => {
  const {
    logEvent,
    eventName,
    metadata,
    intent,
    sourceName,
    plaidIntegrationVersion,
  } = params;
  const sessionId = isWebEventMetadata(metadata)
    ? metadata?.link_session_id
    : metadata?.linkSessionId;

  if (sessionId) {
    if (sessionId !== currentSessionId) {
      firedEvents.clear();
      currentSessionId = sessionId;
    }

    const dedupKey = `${sessionId}:${eventName}`;
    if (firedEvents.has(dedupKey)) {
      return;
    }
    firedEvents.add(dedupKey);
  }

  const institutionId = isWebEventMetadata(metadata)
    ? metadata?.institution_id
    : metadata?.institutionId;
  const institutionName = isWebEventMetadata(metadata)
    ? metadata?.institution_name
    : metadata?.institutionName;

  if (eventName === 'OPEN') {
    logEvent(PLAID_LINK_OPENED, {
      intent: intent?.intent,
      link_session_id: sessionId,
      source_name: sourceName,
      plaidIntegrationVersion: plaidIntegrationVersion ?? versionUnknown,
    });
  }

  const STEP_ALLOWLIST: Record<string, string> = {
    SELECT_INSTITUTION: 'SELECT_INSTITUTION',
    SUBMIT_CREDENTIALS: 'SUBMIT_CREDENTIALS',
    VERIFY_PHONE: 'VERIFY_PHONE',
    SUBMIT_PHONE: 'SUBMIT_PHONE',
    SUBMIT_OTP: 'SUBMIT_OTP',
    SELECT_BRAND: 'SELECT_BRAND',
    MATCHED_SELECT_VERIFY_METHOD: 'MATCHED_SELECT_VERIFY_METHOD', // not seen
    MATCHED_SELECT_INSTITUTION: 'MATCHED_SELECT_INSTITUTION', // not seen
  };

  const stepName = STEP_ALLOWLIST[eventName];
  if (stepName) {
    logEvent(PLAID_LINK_STEP_COMPLETED, {
      intent: intent?.intent,
      step_name: stepName,
      link_session_id: sessionId,
      institution_name: institutionName,
      institution_id: institutionId,
      source_name: sourceName,
      plaidIntegrationVersion: plaidIntegrationVersion ?? versionUnknown,
    });
  }

  const viewName = isWebEventMetadata(metadata)
    ? metadata.view_name
    : metadata.viewName;

  // HANDOFF for native, viewName for web
  if (
    eventName === 'HANDOFF' ||
    (eventName === 'TRANSITION_VIEW' && viewName === 'CONNECTED')
  ) {
    logEvent(PLAID_LINK_STEP_COMPLETED, {
      intent: intent?.intent,
      step_name: 'CONNECTED',
      link_session_id: sessionId,
      institution_name: institutionName,
      institution_id: institutionId,
      source_name: sourceName,
      plaidIntegrationVersion: plaidIntegrationVersion ?? versionUnknown,
    });
  }
};
